import {
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core';
import { ApprovalsService, UsersService } from 'api-clients/monolith';
import React, { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';

import GlobalNav from 'components/GlobalNav';
import { useApiService } from 'hooks/useApiService';
import { useDebounce } from 'hooks/useDebounce';

import { CatalogResponse, CatalogState } from './conditionForm';
import { DeleteRuleDialog } from './DeleteRuleDialog';
import messages from './messages';
import { RuleEditorPanel, UserOption } from './RuleEditorPanel';
import { RuleRow } from './RuleRow';
import { useStyles } from './styles';
import { ApprovalRule, OFFER, OPENING, SubjectType } from './types';
import { useApprovalRules } from './useApprovalRules';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * The approval rules editor (AXHE-3945).
 *
 * Deliberately a new surface rather than a tab on the legacy `OpeningApproval`
 * page. That page is openings-only and backed by the legacy approver-group
 * tables; this one is entity-agnostic and backed by `Approvals::Rule`. Keeping
 * them apart means the old page stays untouched and running during cutover, so
 * the two can be compared side by side — and rules the legacy backfill still
 * owns are shown here read-only rather than being editable in two places at
 * once (see `managed_by_legacy_group`).
 */
export const ApprovalRules: React.FC = () => {
  const styles = useStyles();
  const intl = useIntl();

  const [subjectType, setSubjectType] = useState<SubjectType>(OFFER);
  // Two pieces of state on purpose: `searchInput` keeps the field responsive on
  // every keystroke, `searchTerm` is what actually reaches the server. Driving
  // the request straight from the input would fire one per character.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSearch = useDebounce(
    useCallback((value: string) => setSearchTerm(value), []),
    SEARCH_DEBOUNCE_MS,
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
    debouncedSearch(event.target.value);
  };

  // Which rule the panel is editing. `undefined` rule with the panel open is a
  // create; a rule is an edit. One piece of state rather than two booleans, so
  // "creating and editing at once" is unrepresentable.
  const [editing, setEditing] = useState<{ rule?: ApprovalRule } | null>(null);
  const [createMenuAnchor, setCreateMenuAnchor] = useState<HTMLElement | null>(null);
  // The rule awaiting delete confirmation, plus how many in-flight approvals
  // that delete would cancel. `pending: null` means the count is still loading.
  const [deleting, setDeleting] = useState<{
    rule: ApprovalRule;
    pending: number | null;
  } | null>(null);
  // Which row's toggle is mid-flight, so only that switch is held rather than
  // the whole list going inert while one rule saves.
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toggleFailed, setToggleFailed] = useState(false);

  const { result, refetch } = useApprovalRules(subjectType, searchTerm);

  // Fetched once for the whole page rather than per panel open: the roster does
  // not change while an admin is writing a rule, and re-requesting it on every
  // open would put a spinner in front of the approver field each time.
  // `useApiService` returns { result, refetch, cancel } — the state is one level
  // in, not the return value itself.
  const { result: userOptionsResult } = useApiService<UserOption[]>(
    useCallback(() => UsersService.getInternalApiUsersPackUserOptions(), []),
  );
  const userOptions =
    userOptionsResult.status === 'ready' ? userOptionsResult.data : [];

  // The typed field catalog the condition builder reads (AXHE-3929). Fetched
  // alongside the roster for the same reason: it is account configuration that
  // does not change while a rule is being written.
  const {
    result: catalogResult,
    refetch: refetchCatalog,
  } = useApiService<CatalogResponse>(
    useCallback(() => ApprovalsService.getInternalApiApprovalsFieldCatalog(), []),
  );

  // Passed down as a state, not flattened to an array. Collapsing loading and
  // failed to `[]` makes them indistinguishable from an account with no fields
  // to gate on — the builder would render an empty picker with no message and no
  // retry, and on an existing rule its stored conditions would render as blank
  // selects. `useMemo` because this is a prop on a keyed panel; a fresh object
  // per render would defeat the memoisation downstream.
  const catalogState: CatalogState = useMemo(() => {
    if (catalogResult.status === 'ready') {
      return {
        status: 'ready',
        catalog: {
          fields: catalogResult.data.fields ?? [],
          // Empty only if the server ever ships no unary operators; it is the
          // server's answer either way rather than a copy kept here.
          unaryOperators: catalogResult.data.unary_operators ?? [],
        },
      };
    }
    return catalogResult.status === 'error'
      ? { status: 'error' }
      : { status: 'loading' };
  }, [catalogResult]);

  /**
   * Open the delete confirmation, then fill in the consequence.
   *
   * The count is fetched when the dialog opens rather than rendered from a
   * hook inside it: the confirmation has to name what the delete destroys
   * BEFORE the admin commits (AXHE-4018), and the dialog stays presentational.
   * The guard on re-entry stops a slow response for one rule from landing in a
   * dialog the admin has since reopened on a different one.
   */
  const requestDelete = async (rule: ApprovalRule) => {
    setDeleting({ rule, pending: null });
    let count = 0;
    try {
      const response = await ApprovalsService.getInternalApiApprovalsRulesPendingRequests(
        rule.id as string,
      );
      count = response.pending_requests ?? 0;
    } catch {
      // The cascade warning is still correct without it; failing to count is not
      // a reason to block a delete the admin explicitly asked for.
      count = 0;
    }
    setDeleting(current =>
      current && current.rule.id === rule.id ? { ...current, pending: count } : current,
    );
  };

  /**
   * Flip a rule active or inactive from its row.
   *
   * Sends only `enabled`. Every other field on a PATCH keeps its current value
   * when omitted — except `levels`, which replaces the whole tree when sent — so
   * the narrow payload is what stops a toggle from rewriting the rule's levels
   * with whatever the list happened to be holding.
   *
   * Not optimistic: this decides whether a compliance gate is running, and a
   * switch that flips locally and silently fails server-side would leave the
   * screen claiming a gate is on when it is off.
   */
  const toggleRule = async (rule: ApprovalRule, enabled: boolean) => {
    setTogglingId(rule.id as string);
    setToggleFailed(false);
    try {
      await ApprovalsService.patchInternalApiApprovalsRules(rule.id as string, {
        rule: { name: rule.name as string, subject_type: rule.subject_type as SubjectType, enabled },
      });
      refetch();
    } catch {
      setToggleFailed(true);
    } finally {
      setTogglingId(null);
    }
  };

  // `reloading` carries the *previous* request's data, and on this page the only
  // thing that triggers a refetch is the filter itself — the toggle or the
  // search term. So the rows it carries always belong to the filter the user
  // just moved away from: switching to Openings would list Offer rules, and a
  // new search would list the unfiltered set, each under a heading claiming
  // otherwise. Showing rows while the request that justifies them is still in
  // flight is presenting stale data as current, so `reloading` is a loading
  // state here, not a data state.
  //
  // Read the flags off the result rather than re-deriving them from `status`:
  // `ReloadingState` already declares `isLoading: true`, and hand-rolling the
  // check is what let the container disagree with its own type in the first
  // place.
  const rules = result.status === 'ready' ? result.data.rules ?? [] : [];
  const { isLoading, isError } = result;
  const isSearching = searchTerm.trim().length > 0;

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage(messages.pageTitle)}</title>
      </Helmet>

      {/* Now that this route is registered and reachable by direct link
          (AXHE-3945), the page needs the recruiter chrome its settings-route
          peers render — OfferLetterTemplates, CustomerAttributes,
          TranslationSettings and UserGroupsManagement all render GlobalNav
          themselves. The Account router only supplies GlobalDrawer, which is
          the mobile nav, so without this a desktop user following a direct
          link had no in-app way out at all: no nav, no breadcrumb, no back.
          GlobalNav handles its own breakpoints internally, so it is rendered
          bare rather than wrapped in Hidden. */}
      <GlobalNav />

      <div className={styles.page}>
        <div className={styles.header}>
          <Typography variant="h4">
            <FormattedMessage {...messages.pageTitle} />
          </Typography>
          {/* A split button rather than a plain one: a rule's subject type is
              fixed at creation and cannot be changed afterwards, so choosing it is
              part of starting, not part of editing. */}
          <Button
            variant="contained"
            color="primary"
            onClick={event => setCreateMenuAnchor(event.currentTarget)}
          >
            <FormattedMessage {...messages.createRule} />
          </Button>
          <Menu
            anchorEl={createMenuAnchor}
            open={Boolean(createMenuAnchor)}
            onClose={() => setCreateMenuAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                setCreateMenuAnchor(null);
                setSubjectType(OFFER);
                setEditing({});
              }}
            >
              <FormattedMessage {...messages.createOfferRule} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setCreateMenuAnchor(null);
                setSubjectType(OPENING);
                setEditing({});
              }}
            >
              <FormattedMessage {...messages.createOpeningRule} />
            </MenuItem>
          </Menu>
        </div>

        {/* Two sides of one rule table, not two features — the toggle filters by
            subject_type. Offers first, matching the design's default view. */}
        <Tabs
          value={subjectType}
          onChange={(_event, value: SubjectType) => setSubjectType(value)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab value={OFFER} label={intl.formatMessage(messages.offersTab)} />
          <Tab value={OPENING} label={intl.formatMessage(messages.openingsTab)} />
        </Tabs>

        <div className={styles.toolbar}>
          <TextField
            // MUI v4 only links the <label> to the <input> when an id is given;
            // without it the field is unlabelled for screen readers and for
            // Testing Library's getByLabelText.
            id="approval-rules-search"
            className={styles.search}
            margin="normal"
            label={intl.formatMessage(messages.searchLabel)}
            value={searchInput}
            onChange={handleSearchChange}
          />
        </div>

        {/* Above the list, not inside it: an error is about the request, not about
            a row, and burying it in the table body reads as "no rules" — which is
            the one thing it does not mean. */}
        {isError && (
          <div className={styles.error} role="alert">
            <Typography variant="body1">
              <FormattedMessage {...messages.loadError} />
            </Typography>
            <Button variant="outlined" onClick={refetch}>
              <FormattedMessage {...messages.retry} />
            </Button>
          </div>
        )}

        {/* A failed toggle is reported here rather than on the row: the switch has
            snapped back to where it was, and without a message that reads as the
            click not registering rather than the save being refused. */}
        {toggleFailed && (
          <div className={styles.error} role="alert">
            <Typography variant="body1">
              <FormattedMessage {...messages.toggleFailed} />
            </Typography>
          </div>
        )}

        {isLoading && (
          <div className={styles.list}>
            <div className={styles.skeletonRow}>
              <CircularProgress size={20} aria-label={intl.formatMessage(messages.pageTitle)} />
            </div>
          </div>
        )}

        {!isLoading && !isError && rules.length === 0 && (
          <div className={styles.empty}>
            {/* A search that found nothing is not the same state as an account
                with no rules: one is recoverable by clearing the search, the other
                needs a rule created. Same blank area, different message. */}
            <Typography variant="h6">
              {isSearching ? (
                <FormattedMessage
                  {...messages.noSearchResultsTitle}
                  values={{ term: searchTerm.trim() }}
                />
              ) : (
                <FormattedMessage {...messages.emptyTitle} />
              )}
            </Typography>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={styles.emptyBody}
            >
              {isSearching ? (
                <FormattedMessage {...messages.noSearchResultsBody} />
              ) : (
                <FormattedMessage {...messages.emptyBody} />
              )}
            </Typography>
          </div>
        )}

        {!isLoading && rules.length > 0 && (
          <div className={styles.list}>
            {rules.map(rule => (
              <RuleRow
                key={rule.id}
                rule={rule}
                toggling={togglingId === rule.id}
                onEdit={() => setEditing({ rule })}
                onToggle={enabled => toggleRule(rule, enabled)}
                onDelete={() => requestDelete(rule)}
              />
            ))}
          </div>
        )}

        {/* Keyed so switching between rules remounts the panel: the form holds the
            draft in its own state, and a reused instance would keep the previously
            opened rule's values while displaying the new rule's title. */}
        {deleting && (
          <DeleteRuleDialog
            rule={deleting.rule}
            pendingRequests={deleting.pending ?? 0}
            pendingLoading={deleting.pending === null}
            onCancel={() => setDeleting(null)}
            onDeleted={() => {
              // Same re-entry guard as the pending-count fetch above. The dialog
              // discards the canceller `mutation()` returns, so dismissing it does
              // not abort the DELETE already in flight: that request still
              // resolves and still calls back. Closing unconditionally would then
              // shut a confirmation the admin has since opened on a *different*
              // rule, with no explanation and that second rule untouched.
              //
              // `deletedRule` is this render's rule, and the in-flight promise
              // captured this render's callback — so it names the rule whose
              // deletion actually completed, not whatever is on screen now.
              const deletedRule = deleting.rule;

              setDeleting(current =>
                current && current.rule.id === deletedRule.id ? null : current,
              );
              refetch();
            }}
          />
        )}

        {editing && (
          <RuleEditorPanel
            key={editing.rule?.id ?? 'new'}
            open
            rule={editing.rule}
            subjectType={subjectType}
            userOptions={userOptions}
            catalog={catalogState}
            onRetryCatalog={refetchCatalog}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              refetch();
            }}
          />
        )}
      </div>
    </>
  );
};

export default ApprovalRules;
